import { AdderTypes, EquipmentType } from "@oneethos/shared"
import { FormInput, SubformProps } from "."
import { AddressInputs } from "../../components/forms"
import ApplicantForm from "./applicant-form"
import { CoborrowerFields } from "./coborrower"
import { ConsumerResourceLinks } from "./consumer-resource-links"
import { LoanSummary } from "../../components/loan-summary"
import { Spinner } from '@fluentui/react'

const ignoreAccountEdit = () => undefined

type ReadOnlyAutoDebitAccountProps = {
  institution?: string
  routingNumber?: string
  accountNumber?: string
}

const ReadOnlyAutoDebitAccount = ({
  institution,
  routingNumber,
  accountNumber,
}: ReadOnlyAutoDebitAccountProps) => <>
  <FormInput
    field='autoDebitInstitution'
    onChange={ignoreAccountEdit}
    disabled
    value={institution || ''}
  />
  <FormInput
    field='autoDebitRoutingNo'
    onChange={ignoreAccountEdit}
    disabled
    value={routingNumber || ''}
  />
  <FormInput
    field='autoDebitAccountNo'
    onChange={ignoreAccountEdit}
    disabled
    value={accountNumber || ''}
  />
</>

export const ReviewApplication = ({
  formData,
  update,
  save,
  saving,
  onNav,
}: SubformProps) => {
  const hasAgreedToConsumerResources = formData.attestConsumerResourceUnderstanding
  const wantsAutoDebit = formData.useAutoDebit === 'yes'

  const handleAutoDebitChoice = (nextChoice: string) => {
    update('useAutoDebit', nextChoice)
    if (formData.useAutoDebit !== 'yes' && nextChoice === 'yes') {
      onNav?.('Auto-Debit Authorization')
    }
  }

  return (
    <div>
      <h2>Review Application</h2>
      <p>
        Almost there! Before submitting your Clean Energy Loan application, please review all details for accuracy.
        If needed, you'll be asked to upload additional documents in the next step.
      </p>
      <div className="alert alert-warning">
        Please note that making changes at this step may affect your prequalification eligibility.
      </div>
      <AddressInputs
        address={formData.installationAddress || {}}
        label="Address where solar panels will be installed"
        onChange={address => {
          update({
            ...formData,
            installationAddress: address,
            userAddress: address
          })
        }}
      />
      <FormInput
        field='solarCost'
        value={formData.solarCost}
        disabled
      />
      {formData.adders?.length ? <div className="row row-cols-1 row-cols-md-2 g-3">
        {formData.adders?.map((adder, index) => (
          <div className="col" key={index}>
            <FormInput
              field='adders'
              label={
                (formData.equipmentType === EquipmentType.TeslaSolar
                  || formData.equipmentType === EquipmentType.TeslaRoof) &&
                  adder.description === AdderTypes.StoragePrice
                  ? 'Powerwall Total Price'
                  : adder.description
              }
              value={(adder.description === AdderTypes.Downpayment
                || adder.description === AdderTypes.RebatesAndOrCredits)
                ? `- ${adder.amount.toString()}` : adder.amount.toString()}
              disabled
            />
          </div>
        ))}
      </div> : null}
      <FormInput
        field='loanAmount'
        value={formData.loanAmount}
        onChange={s => update('loanAmount', s)}
        disabled
      />
      <FormInput
        field='avgMonthlyUtilityBill'
        value={formData.avgMonthlyUtilityBill}
        onChange={s => update('avgMonthlyUtilityBill', s)}
      />

      <h3>Borrower Information</h3>
      <ApplicantForm
        applicant={formData}
        update={update}
        address={formData.userAddress}
        onAddressChange={addr => update('userAddress', addr)}
        disableCoreChanges={true}
        verification={formData.personaVerifications?.borrower}
      />

      <FormInput
        field='statedGrossAnnualIncome'
        value={formData.statedGrossAnnualIncome}
        onChange={s => update('statedGrossAnnualIncome', s)}
        disabled={true}
      />

      {formData.isJointApp && (
        <CoborrowerFields formData={formData} update={update} summary />
      )}

      <div className="alert alert-secondary">
        Changes to Income Verification documents can be made
        by navigating to the Income Verification step.
      </div>

      <div className="form-group">
        <label>I'd like to sign up for auto debit</label>
        <select
          className="form-select"
          value={formData.useAutoDebit || 'yes'}
          onChange={ev => handleAutoDebitChoice(ev.target.value)}
        >
          <option value='no'>No</option>
          <option value='yes'>Yes</option>
        </select>
      </div>

      {wantsAutoDebit ? (
        <ReadOnlyAutoDebitAccount
          institution={formData.autoDebitInstitution}
          routingNumber={formData.autoDebitRoutingNo?.toString()}
          accountNumber={formData.autoDebitAccountNo}
        />
      ) : null}
      <div className="my-4 d-flex flex-column gap-2">
        <h3>Loan Summary</h3>
        <LoanSummary formData={formData} showApr={false} />
        <ConsumerResourceLinks formData={formData} update={update} />
      </div>

      <div className="d-flex justify-content-center">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => save()}
          disabled={saving || !hasAgreedToConsumerResources?.agrees}
        >
          {saving ? <Spinner /> : 'Continue'}
        </button>
      </div>
    </div>
  )
}

export default ReviewApplication
